import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  CreateGroupClassPayload,
  CreateGroupTemplatePayload,
  EmptyResponse,
  FilterByQuery,
  GroupClass,
  GroupClassInstructor,
  GroupTemplate,
  IncludeQuery,
  LinkGroupClassParticipantPayload,
  PaginationQuery,
  Response,
  SortByQuery,
  UpdateGroupClassPayload,
  UpdateGroupTemplatePayload,
} from "~/types/api";

export function createGroupClassApi({
  getRequest,
  postRequest,
  deleteRequest,
  getPaginationRequest,
}: ApiRequestHelpers) {
  const groupClassInstructors = (locationId: number) =>
    getRequest<{ data: GroupClassInstructor[] }>(`/group-classes/instructors?location_id=${locationId}`);
  /** Fetches a paginated list of group class templates. */
  const groupTemplates = (
    pagination: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
  ) => getPaginationRequest<GroupTemplate>("/group-class-templates", pagination, sortBy, includes, filterBy);

  /** Fetches a single group class template by ID. */
  const groupTemplate = (id: string) => getRequest<Response<GroupTemplate>>(`/group-class-templates/${id}`);

  /** Creates a new group class template. */
  const createGroupTemplate = (data: CreateGroupTemplatePayload) =>
    postRequest<Response<GroupTemplate>>("/group-class-templates", data);

  /** Updates an existing group class template. */
  const updateGroupTemplate = (id: string, data: UpdateGroupTemplatePayload) =>
    postRequest<Response<GroupTemplate>>(`/group-class-templates/${id}`, data);

  /** Deletes a group class template. */
  const deleteGroupTemplate = (id: string) => deleteRequest<EmptyResponse>(`/group-class-templates/${id}`);

  /** Fetches group classes within a date range. */
  const groupClasses = (startAt: string, endAt: string, additionalParams?: Record<string, string>) => {
    let url = `/group-classes?start_at=${startAt}&end_at=${endAt}`;

    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, value]) => {
        if (value) {
          url += `&${key}=${encodeURIComponent(value)}`;
        }
      });
    }

    return getRequest<GroupClass[]>(url);
  };

  /** Fetches a single group class by ID. */
  const groupClass = (id: string) => getRequest<GroupClass>(`/group-classes/${id}`);

  /** Fetches a single recurring group class by ID. */
  const recurringGroupClass = (id: string) => getRequest<GroupClass>(`/group-classes/recurring/${id}`);

  /** Creates a new one-off group class. */
  const createGroupClass = (data: CreateGroupClassPayload) => postRequest<GroupClass>("/group-classes", data);

  /** Creates a new recurring group class series. */
  const createRecurringGroupClass = (data: CreateGroupClassPayload) =>
    postRequest<GroupClass>("/group-classes/recurring", data);

  /** Fetches recurring group classes, optionally filtered by date range. */
  const recurringGroupClasses = (startDate?: string, endDate?: string, additionalParams?: Record<string, string>) => {
    const params = new URLSearchParams();
    if (startDate) params.append("start_at", startDate);
    if (endDate) params.append("end_at", endDate);
    if (additionalParams) {
      Object.entries(additionalParams).forEach(([key, value]) => {
        params.append(key, value);
      });
    }
    const queryString = params.toString();
    return getRequest<GroupClass[]>(`/group-classes/recurring${queryString ? `?${queryString}` : ""}`);
  };

  /** Updates a one-off group class. */
  const updateGroupClass = (id: string, data: UpdateGroupClassPayload) =>
    postRequest<GroupClass>(`/group-classes/${id}`, data);

  /** Updates a recurring group class, optionally applying changes to the entire series or future instances. */
  const updateRecurringGroupClass = (
    id: string,
    data: UpdateGroupClassPayload,
    updateEntireSeries: boolean = false,
    updateThisAndFuture: boolean = false,
  ) => {
    const payload = { ...data } as UpdateGroupClassPayload;
    if (updateEntireSeries) {
      payload.update_entire_series = true;
    }
    if (updateThisAndFuture) {
      payload.update_this_and_future = true;
    }
    return postRequest<GroupClass>(`/group-classes/recurring/${id}`, payload);
  };

  /** Deletes a one-off group class. */
  const deleteGroupClass = (id: string) => deleteRequest<EmptyResponse>(`/group-classes/${id}`);

  /** Deletes a recurring group class, optionally removing the entire series or future instances. */
  const deleteRecurringGroupClass = (
    id: string,
    deleteEntireSeries: boolean = false,
    deleteThisAndFuture: boolean = false,
  ) => {
    const params = new URLSearchParams();
    if (deleteEntireSeries) {
      params.append("delete_entire_series", "true");
    }
    if (deleteThisAndFuture) {
      params.append("delete_this_and_future", "true");
    }
    const queryString = params.toString();
    return deleteRequest<EmptyResponse>(`/group-classes/recurring/${id}${queryString ? `?${queryString}` : ""}`);
  };

  /** Links a participant to a one-off group class. */
  const linkParticipantToGroupClass = (groupClassId: string, data: LinkGroupClassParticipantPayload) =>
    postRequest<GroupClass>(`/group-classes/${groupClassId}/link-participant`, data);

  /** Links a participant to a recurring group class series. */
  const linkParticipantToRecurringGroupClass = (groupClassId: string, data: LinkGroupClassParticipantPayload) => {
    return postRequest<GroupClass>(`/group-classes/recurring/${groupClassId}/link-participant`, data);
  };

  /** Unlinks a participant from a one-off group class. */
  const unlinkParticipantFromGroupClass = (groupClassId: string, participantId: string) =>
    deleteRequest<EmptyResponse>(`/group-classes/${groupClassId}/participants/${participantId}`);

  /** Unlinks a participant from a recurring group class, optionally from the entire series or future instances. */
  const unlinkParticipantFromRecurringGroupClass = (
    groupClassId: string,
    participantId: string,
    unlinkFromEntireSeries: boolean = false,
    unlinkThisAndFuture: boolean = false,
  ) => {
    const params = new URLSearchParams();
    if (unlinkFromEntireSeries) {
      params.append("unlink_from_entire_series", "true");
    }
    if (unlinkThisAndFuture) {
      params.append("unlink_this_and_future", "true");
    }
    const queryString = params.toString();
    return deleteRequest<EmptyResponse>(
      `/group-classes/recurring/${groupClassId}/participants/${participantId}${queryString ? `?${queryString}` : ""}`,
    );
  };

  return {
    groupClassInstructors,
    groupTemplates,
    groupTemplate,
    createGroupTemplate,
    updateGroupTemplate,
    deleteGroupTemplate,
    groupClasses,
    groupClass,
    recurringGroupClass,
    createGroupClass,
    createRecurringGroupClass,
    recurringGroupClasses,
    updateGroupClass,
    updateRecurringGroupClass,
    deleteGroupClass,
    deleteRecurringGroupClass,
    linkParticipantToGroupClass,
    linkParticipantToRecurringGroupClass,
    unlinkParticipantFromGroupClass,
    unlinkParticipantFromRecurringGroupClass,
  };
}

export type GroupClassApi = ReturnType<typeof createGroupClassApi>;
